import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { SessionEvidenceI } from 'Api/axios/sessions-api';
import { s3 } from 'Services/AWS/s3';
import { formatSessionDate } from 'Pages/EditCourseSessions/utils/session-datetime';

interface Props {
  evidences: SessionEvidenceI[];
  onDownload: (fileUrl: string, fileName: string) => Promise<void> | void;
  menuLabel?: string;
  emptyText?: string;
}

type SignedUrlState = 'idle' | 'loading' | 'error' | string;

const isImage = (ev: SessionEvidenceI) =>
  ev.fileType?.startsWith('image/') || /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(ev.fileName);

const formatBytes = (bytes: number | null): string => {
  if (bytes == null || bytes <= 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return ` · ${kb.toFixed(0)} KB`;
  return ` · ${(kb / 1024).toFixed(1)} MB`;
};

const getFileTypeLabel = (ev: SessionEvidenceI): string => {
  if (ev.fileType) {
    if (ev.fileType.startsWith('image/')) return 'Imagen';
    if (ev.fileType === 'application/pdf') return 'PDF';
    if (ev.fileType.includes('word')) return 'Word';
    if (ev.fileType.includes('spreadsheet') || ev.fileType.includes('excel')) return 'Excel';
    if (ev.fileType.includes('presentation') || ev.fileType.includes('powerpoint')) return 'PowerPoint';
  }
  return ev.fileName.split('.').pop()?.toUpperCase() ?? 'Archivo';
};

export default function EmployeeEvidencesMenu({ evidences, onDownload, menuLabel, emptyText = '—' }: Props) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [signedUrls, setSignedUrls] = useState<Record<string, SignedUrlState>>({});

  useEffect(() => {
    if (!open) return;
    setSignedUrls(prev => {
      const next = { ...prev };
      evidences.forEach(ev => {
        if (!next[ev.id]) next[ev.id] = 'loading';
      });
      return next;
    });

    evidences.forEach(async ev => {
      if (signedUrls[ev.id] && signedUrls[ev.id] !== 'idle') return;
      try {
        const res = await s3.getSignedUrl(ev.fileUrl);
        const url = res?.signedUrl ?? null;
        setSignedUrls(prev => ({ ...prev, [ev.id]: url ?? 'error' }));
      } catch {
        setSignedUrls(prev => ({ ...prev, [ev.id]: 'error' }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!evidences || evidences.length === 0) {
    return <span aria-label="sin evidencias">{emptyText}</span>;
  }

  const active = evidences[activeIndex];
  const signedUrl = active ? signedUrls[active.id] : undefined;
  const total = evidences.length;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex(0);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  return (
    <>
      <Tooltip title={menuLabel ?? 'Ver evidencias'} placement="top" arrow>
        <IconButton size="small" aria-label={menuLabel ?? 'evidencias'} onClick={handleOpen}>
          <AttachFileIcon fontSize="small" />
          <span style={{ marginLeft: 4, fontSize: 12 }}>{total}</span>
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth onClick={e => e.stopPropagation()}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              {active?.fileName}
            </Typography>
            {total > 1 && (
              <Typography variant="body2" color="text.secondary">
                ({activeIndex + 1} / {total})
              </Typography>
            )}
          </Box>
          <Tooltip title="Descargar">
            <IconButton size="small" onClick={() => active && onDownload(active.fileUrl, active.fileName)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={handleClose} aria-label="cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, position: 'relative', minHeight: 300 }}>
          {/* Navigation arrows */}
          {total > 1 && (
            <>
              <IconButton
                size="small"
                onClick={() => setActiveIndex(i => Math.max(0, i - 1))}
                disabled={activeIndex === 0}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'rgba(255,255,255,0.85)',
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setActiveIndex(i => Math.min(total - 1, i + 1))}
                disabled={activeIndex === total - 1}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'rgba(255,255,255,0.85)',
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          {/* Preview area */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, bgcolor: '#f6f7fc' }}
          >
            {signedUrl === 'loading' || !signedUrl ? (
              <CircularProgress size={32} />
            ) : signedUrl === 'error' ? (
              <Typography color="error" fontSize={13}>
                No se pudo cargar la vista previa
              </Typography>
            ) : active && isImage(active) ? (
              <Box
                component="img"
                src={signedUrl}
                alt={active.fileName}
                sx={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}>
                <InsertDriveFileIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                <Typography fontSize={13} color="text.secondary">
                  {getFileTypeLabel(active!)}
                  {formatBytes(active!.fileSize)}
                </Typography>
                <Typography fontSize={12} color="text.disabled">
                  Vista previa no disponible · usa el botón de descarga
                </Typography>
              </Box>
            )}
          </Box>

          {/* Thumbnail strip */}
          {total > 1 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                p: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                overflowX: 'auto',
              }}
            >
              {evidences.map((ev, idx) => {
                const url = signedUrls[ev.id];
                const imgUrl = url && url !== 'loading' && url !== 'error' && isImage(ev) ? url : null;
                return (
                  <Box
                    key={ev.id}
                    onClick={() => setActiveIndex(idx)}
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: idx === activeIndex ? 'primary.main' : 'divider',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#f0f0f0',
                    }}
                  >
                    {imgUrl ? (
                      <Box
                        component="img"
                        src={imgUrl}
                        alt={ev.fileName}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <InsertDriveFileIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Metadata footer */}
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography fontSize={11.5} color="text.secondary">
              {active && formatSessionDate(active.createdAt)}
              {active && formatBytes(active.fileSize)}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
